import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { memorialThemes } from '../../styles/themes';
import { writeFetch, getSession } from '../../api';
import { secureUpload } from '../../services/s3UploadService';

const {
  FiLayout, FiLock, FiUnlock, FiGlobe, FiUsers, FiEyeOff,
  FiMusic, FiWind, FiSmile, FiVolume2, FiVolumeX,
  FiCheck, FiSave, FiRefreshCw, FiInfo, FiDroplet,
  FiType, FiSliders, FiShield, FiImage, FiBookOpen,
  FiUploadCloud, FiTrash2, FiHeadphones, FiCloud
} = FiIcons;

// ── Persistence ───────────────────────────────────────────────────────────────
const STORAGE_KEY = 'em_memorial_defaults';
const load = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; } };

const defaultBanners = [
  { id: 'b1', url: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=2076&auto=format&fit=crop', name: 'Autumn Leaves' },
  { id: 'b2', url: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?q=80&w=2070&auto=format&fit=crop', name: 'Ocean Sunset' },
  { id: 'b3', url: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=2080&auto=format&fit=crop', name: 'Urban Art' },
  { id: 'b4', url: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=2070&auto=format&fit=crop', name: 'Spring Forest' },
  { id: 'b5', url: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop', name: 'Serene Lake' },
  { id: 'b6', url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop', name: 'Mountain Peak' },
];

const loadPresets = () => {
    try {
        const saved = localStorage.getItem('em_premium_presets');
        return saved ? JSON.parse(saved) : defaultBanners;
    } catch {
        return defaultBanners;
    }
};

// ── Data ──────────────────────────────────────────────────────────────────────
const PRIVACY_OPTIONS = [
  { id: 'public',   icon: FiGlobe,   label: 'Public',           desc: 'Visible to everyone on the platform and search engines.' },
  { id: 'family',   icon: FiUsers,   label: 'Family & Friends', desc: 'Only people with the memorial link can view it.' },
  { id: 'private',  icon: FiLock,    label: 'Private',          desc: 'Only the memorial owner and approved collaborators.' },
];

const SOUNDSCAPE_CATEGORIES = [
  { id: 'hymns',         icon: FiMusic,      label: 'Hymns'         },
  { id: 'instrumentals', icon: FiHeadphones, label: 'Instrumentals' },
  { id: 'ambience',      icon: FiWind,       label: 'Ambience'      },
];

const SOUNDSCAPES = [
  ...SOUNDSCAPE_CATEGORIES,
  { id: 'none', icon: FiVolumeX, label: 'No Soundscape' },
];

const TRIBUTE_LAYOUTS = [
  { id: 'cards',    label: 'Card Grid',    desc: 'Tributes shown as elegant cards in a masonry grid.' },
  { id: 'timeline', label: 'Timeline',     desc: 'Chronological vertical timeline of tributes.' },
  { id: 'minimal',  label: 'Minimal List', desc: 'Clean, simple list with no imagery.' },
];

const SECTIONS = [
  { id: 'privacy',    label: 'Privacy',       icon: FiShield   },
  { id: 'theme',      label: 'Theme',         icon: FiLayout   },
  { id: 'banner',     label: 'Banner',        icon: FiImage    },
  { id: 'soundscape', label: 'Soundscape',    icon: FiVolume2  },
  { id: 'features',   label: 'Features',      icon: FiSliders  },
];

// ── Shared sub-components ─────────────────────────────────────────────────────
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="bg-white rounded-[32px] border border-brand-dark p-8 space-y-6">
    <div className="flex items-center gap-4 pb-5 border-b border-brand-dark">
      <div className="p-3 rounded-2xl bg-brand-light">
        <SafeIcon icon={icon} className="w-5 h-5 text-accent-navy" />
      </div>
      <div>
        <h3 className="font-serif text-lg text-accent-navy">{title}</h3>
        {subtitle && <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, value, onChange, tag = null }) => (
  <div className="flex items-center justify-between gap-4 p-5 rounded-2xl border border-brand-dark bg-brand-light hover:border-accent-gold/50 transition-colors group">
    <div className="flex-grow min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-bold text-sm text-accent-navy group-hover:text-accent-gold transition-colors">{label}</p>
        {tag && (
          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-100 text-green-700">{tag}</span>
        )}
      </div>
      <p className="text-[11px] text-text-muted leading-relaxed mt-0.5">{desc}</p>
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-12 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${value ? 'bg-accent-gold' : 'bg-brand-dark'}`}
    >
      <motion.div
        animate={{ x: value ? 24 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
      />
    </button>
  </div>
);

const SaveToast = ({ show, message = 'Defaults Saved' }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[200] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">{message}</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Stat pill ─────────────────────────────────────────────────────────────────
const StatPill = ({ icon, label, value, color }) => (
  <div className="flex items-center gap-3 p-5 rounded-[20px] border border-brand-dark bg-white">
    <div className={`p-2.5 rounded-xl ${color}`}>
      <SafeIcon icon={icon} className="w-4 h-4 text-white" />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{label}</p>
      <p className="text-xl font-serif text-accent-navy">{value}</p>
    </div>
  </div>
);

// ── MAIN ──────────────────────────────────────────────────────────────────────
const MemorialDefaults = () => {
  const saved = load();

  // Privacy
  const [defaultPrivacy, setDefaultPrivacy]     = useState(saved.defaultPrivacy     ?? 'public');
  const [allowPrivacyChange, setAllowPrivacyChange] = useState(saved.allowPrivacyChange ?? true);
  const [requireApprovalToView, setRequireApprovalToView] = useState(saved.requireApprovalToView ?? false);

  // Theme
  const [defaultThemeId, setDefaultThemeId]     = useState(saved.defaultThemeId     ?? 'heritage');
  const [allowThemeChange, setAllowThemeChange]  = useState(saved.allowThemeChange   ?? true);
  const [defaultFont, setDefaultFont]            = useState(saved.defaultFont        ?? 'font-serif');

  // Soundscape
  const [defaultSoundscape, setDefaultSoundscape] = useState(saved.defaultSoundscape ?? 'none');
  const [soundscapeAutoplay, setSoundscapeAutoplay] = useState(saved.soundscapeAutoplay ?? false);
  const [allowSoundscapeChange, setAllowSoundscapeChange] = useState(saved.allowSoundscapeChange ?? true);
  
  const [uploadingAudioCat, setUploadingAudioCat] = useState(null);
  const [urlInputCat, setUrlInputCat]             = useState(null);
  const [urlInputLabel, setUrlInputLabel]         = useState('');
  const [urlInputValue, setUrlInputValue]         = useState('');

  // Features
  const [enableGuestbook, setEnableGuestbook]   = useState(saved.enableGuestbook    ?? true);
  const [enableGallery, setEnableGallery]        = useState(saved.enableGallery      ?? true);
  const [enableDonations, setEnableDonations]    = useState(saved.enableDonations    ?? true);
  const [enableLiveStream, setEnableLiveStream]  = useState(saved.enableLiveStream   ?? false);
  const [enableMilestones, setEnableMilestones]  = useState(saved.enableMilestones   ?? true);
  const [enableVisitorMap, setEnableVisitorMap]  = useState(saved.enableVisitorMap   ?? true);
  const [tributeLayout, setTributeLayout]        = useState(saved.tributeLayout      ?? 'cards');

  // Banner Presets
  const [presets, setPresets] = useState(loadPresets());
  const [isUploadingPreset, setIsUploadingPreset] = useState(false);

  // UI
  const [activeSection, setActiveSection]        = useState('privacy');
  const [showToast, setShowToast]                = useState(false);
  const [toastMsg, setToastMsg]                  = useState('Defaults Saved');

  const handleSave = () => {
    const payload = {
      defaultPrivacy, allowPrivacyChange, requireApprovalToView,
      defaultThemeId, allowThemeChange, defaultFont,
      defaultSoundscape, soundscapeAutoplay, allowSoundscapeChange,
      enableGuestbook, enableGallery, enableDonations,
      enableLiveStream, enableMilestones, enableVisitorMap, tributeLayout,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('memorial_defaults_updated', { detail: payload }));
    setToastMsg('Defaults Saved');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleReset = () => { localStorage.removeItem(STORAGE_KEY); window.location.reload(); };

  const handlePresetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingPreset(true);
    try {
      const url = URL.createObjectURL(file);
      const newPreset = { 
        id: 'b' + Date.now(), 
        url, 
        name: file.name.split('.')[0] || 'Custom Preset' 
      };
      const updated = [...presets, newPreset];
      setPresets(updated);
      localStorage.setItem('em_premium_presets', JSON.stringify(updated));
      window.dispatchEvent(new Event('em_presets_updated'));
    } catch (err) {
      console.warn('Failed to upload preset:', err);
    } finally {
      setIsUploadingPreset(false);
    }
  };

  const handleDeletePreset = (id) => {
    const updated = presets.filter(p => p.id !== id);
    setPresets(updated);
    localStorage.setItem('em_premium_presets', JSON.stringify(updated));
    window.dispatchEvent(new Event('em_presets_updated'));
  };

  const handleAudioUpload = async (e, catId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAudioCat(catId);
    try {
      const user = getSession();
      const ownerId = user?.id || 'admin';
      const { s3Key, s3Url } = await secureUpload({
        file,
        ownerId,
        category: 'soundscape'
      });

      await writeFetch('create/soundscape_tracks_1776430731351', {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(),
          owner_id: ownerId,
          label: file.name.split('.')[0] || `${SOUNDSCAPE_CATEGORIES.find(c => c.id === catId)?.label} Track`,
          description: `Uploaded preset track`,
          category: catId,
          s3_key: s3Key,
          s3_url: s3Url,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'audio/mpeg',
          is_active: 1,
          sort_order: 0,
        })
      });
      setToastMsg('Audio Track Uploaded');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.warn('Upload failed:', err);
      alert('Failed to upload audio track.');
    } finally {
      setUploadingAudioCat(null);
      e.target.value = null;
    }
  };

  const handleUrlSubmit = async (catId) => {
    if (!urlInputLabel.trim() || !urlInputValue.trim()) {
      alert("Please provide both a label and a valid URL.");
      return;
    }
    setUploadingAudioCat(catId);
    try {
      const user = getSession();
      const ownerId = user?.id || 'admin';
      await writeFetch('create/soundscape_tracks_1776430731351', {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(),
          owner_id: ownerId,
          label: urlInputLabel.trim(),
          description: `External URL track`,
          category: catId,
          s3_key: null,
          s3_url: urlInputValue.trim(),
          file_name: 'External URL',
          file_size: 0,
          file_type: 'audio/mpeg',
          is_active: 1,
          sort_order: 0,
        })
      });
      setToastMsg('Audio URL Saved');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      setUrlInputCat(null);
    } catch (err) {
      console.warn('Upload failed:', err);
      alert('Failed to save audio URL.');
    } finally {
      setUploadingAudioCat(null);
    }
  };

  const activeTheme = memorialThemes[defaultThemeId] || memorialThemes.heritage;

  return (
    <div className="space-y-8">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatPill icon={FiGlobe}    label="Total Memorials"     value="1,284" color="bg-accent-navy" />
        <StatPill icon={FiLock}     label="Private Memorials"   value="312"   color="bg-violet-500"  />
        <StatPill icon={FiUsers}    label="Family-Only"         value="487"   color="bg-blue-500"    />
        <StatPill icon={FiLayout}   label="Using Heritage Theme" value="641"  color="bg-accent-gold" />
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-4 p-5 rounded-[24px] border-2 bg-blue-50 border-blue-200">
        <div className="p-2.5 rounded-xl bg-blue-500 flex-shrink-0">
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="font-black text-xs uppercase tracking-widest text-blue-800">
            These are Global Defaults — applied to all newly created memorials
          </p>
          <p className="text-[11px] mt-0.5 text-blue-600">
            Existing memorials are not affected. Memorial owners can customise their own settings unless an override lock is enabled below.
          </p>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-1">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
          >
            <SafeIcon icon={s.icon} className="w-3.5 h-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >

          {/* ── PRIVACY ─────────────────────────────────────────────────── */}
          {activeSection === 'privacy' && (
            <SectionCard icon={FiShield} title="Default Privacy Settings" subtitle="Controls who can view new memorials">
              <div className="space-y-3">
                {PRIVACY_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDefaultPrivacy(opt.id)}
                    className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all group ${defaultPrivacy === opt.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-brand-light hover:border-accent-gold/50'}`}
                  >
                    <div className={`p-3 rounded-xl flex-shrink-0 ${defaultPrivacy === opt.id ? 'bg-accent-gold text-white' : 'bg-white text-text-muted'}`}>
                      <SafeIcon icon={opt.icon} className="w-4 h-4" />
                    </div>
                    <div className="flex-grow">
                      <p className={`font-bold text-sm ${defaultPrivacy === opt.id ? 'text-accent-navy' : 'text-text-muted'}`}>{opt.label}</p>
                      <p className="text-[10px] text-text-muted leading-relaxed mt-0.5">{opt.desc}</p>
                    </div>
                    {defaultPrivacy === opt.id && (
                      <div className="w-6 h-6 rounded-full bg-accent-gold flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Additional Privacy Controls</p>
                <ToggleRow
                  label="Allow Owners to Change Privacy Setting"
                  desc="Memorial creators can adjust visibility after creation. Disable to enforce the default globally."
                  value={allowPrivacyChange}
                  onChange={setAllowPrivacyChange}
                  tag="Recommended"
                />
                <ToggleRow
                  label="Require Admin Approval to View"
                  desc="All memorial pages require admin approval before becoming publicly accessible."
                  value={requireApprovalToView}
                  onChange={setRequireApprovalToView}
                />
              </div>

              {/* Summary card */}
              <div className="bg-accent-navy rounded-[24px] p-6 flex items-center gap-4">
                <div className="p-3 bg-white/10 rounded-2xl flex-shrink-0">
                  <SafeIcon icon={PRIVACY_OPTIONS.find(o => o.id === defaultPrivacy)?.icon || FiGlobe} className="w-5 h-5 text-accent-gold" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Active Default</p>
                  <p className="text-white font-serif text-lg">{PRIVACY_OPTIONS.find(o => o.id === defaultPrivacy)?.label}</p>
                  <p className="text-white/50 text-[10px] mt-0.5">{PRIVACY_OPTIONS.find(o => o.id === defaultPrivacy)?.desc}</p>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── THEME ───────────────────────────────────────────────────── */}
          {activeSection === 'theme' && (
            <SectionCard icon={FiLayout} title="Default Theme & Visual Identity" subtitle="Preset applied to every new memorial on creation">
              {/* Preset grid */}
              <div className="space-y-3">
                {Object.values(memorialThemes).filter(t => !t.isCustom).map(theme => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => setDefaultThemeId(theme.id)}
                    className={`w-full flex items-center justify-between p-5 rounded-2xl border-2 transition-all ${defaultThemeId === theme.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-brand-light hover:border-accent-gold/50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.primary }} />
                        <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.accent }} />
                      </div>
                      <div className="text-left">
                        <p className={`font-bold text-sm ${defaultThemeId === theme.id ? 'text-accent-navy' : 'text-text-muted'}`}>{theme.name}</p>
                        <p className="text-[9px] font-black uppercase tracking-widest text-text-muted opacity-60">
                          {theme.font.split('-')[1]} · {theme.id}
                        </p>
                      </div>
                    </div>
                    {defaultThemeId === theme.id && (
                      <div className="w-6 h-6 rounded-full bg-accent-gold flex items-center justify-center">
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Default typography */}
              <div className="space-y-3 pt-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Default Typography</p>
                <div className="grid grid-cols-2 gap-3">
                  {['font-serif', 'font-sans'].map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setDefaultFont(f)}
                      className={`py-4 rounded-2xl border-2 font-bold text-xs uppercase tracking-widest transition-all ${defaultFont === f ? 'bg-accent-navy border-accent-navy text-white shadow-lg' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
                    >
                      {f.split('-')[1]}
                    </button>
                  ))}
                </div>
              </div>

              <ToggleRow
                label="Allow Owners to Change Theme"
                desc="Memorial creators can pick a different preset or build a custom theme. Disable to lock the default."
                value={allowThemeChange}
                onChange={setAllowThemeChange}
                tag="Recommended"
              />

              {/* Live preview strip */}
              <div className="rounded-[24px] overflow-hidden border border-brand-dark">
                <div className="p-5 flex items-center gap-3" style={{ backgroundColor: activeTheme.primary }}>
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: activeTheme.accent }} />
                  <div>
                    <p className="text-white text-xs font-black uppercase tracking-widest opacity-60">Preview</p>
                    <p className={`text-white font-bold ${activeTheme.font}`}>{activeTheme.name} Theme</p>
                  </div>
                </div>
                <div className="p-5 flex gap-3" style={{ backgroundColor: activeTheme.background }}>
                  <div className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: activeTheme.accent }} />
                  <p className={`text-xs leading-relaxed ${activeTheme.font}`} style={{ color: activeTheme.primary }}>
                    In loving memory — a sanctuary crafted with care and intention for those who remain.
                  </p>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── BANNER ──────────────────────────────────────────────────── */}
          {activeSection === 'banner' && (
            <SectionCard icon={FiImage} title="Banner Settings" subtitle="Manage default banner presets available to memorial creators">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Premium & Free Presets</p>
                  <label className="btn-secondary py-2 px-4 text-xs inline-flex items-center gap-2 cursor-pointer border-brand-dark">
                    {isUploadingPreset ? (
                      <div className="w-4 h-4 border-2 border-accent-navy/30 border-t-accent-navy rounded-full animate-spin" />
                    ) : (
                      <><SafeIcon icon={FiUploadCloud} /> Upload Preset</>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handlePresetUpload} disabled={isUploadingPreset} />
                  </label>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {presets.map(banner => (
                    <div key={banner.id} className="relative h-20 rounded-xl overflow-hidden border border-brand-dark group">
                      <img src={banner.url} alt={banner.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2 transition-opacity">
                        <span className="text-white text-[9px] font-bold truncate">{banner.name}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => handleDeletePreset(banner.id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500/90 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all scale-90 hover:scale-100 shadow-lg"
                        title="Delete Preset"
                      >
                        <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                {presets.length === 0 && (
                  <div className="p-8 text-center bg-brand-light rounded-2xl border border-dashed border-brand-dark">
                    <SafeIcon icon={FiImage} className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-50" />
                    <p className="text-xs font-bold text-accent-navy">No presets available</p>
                    <p className="text-[10px] text-text-muted">Upload a banner image to provide creators with default options.</p>
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {/* ── SOUNDSCAPE ──────────────────────────────────────────────── */}
          {activeSection === 'soundscape' && (
            <>
              <SectionCard icon={FiVolume2} title="Default Soundscape Preference" subtitle="Ambient audio setting pre-selected for new memorials">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SOUNDSCAPES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setDefaultSoundscape(s.id)}
                      className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${defaultSoundscape === s.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-brand-light hover:border-accent-gold/50'}`}
                    >
                      <div className={`p-3 rounded-xl ${defaultSoundscape === s.id ? 'bg-accent-gold text-white' : 'bg-white text-text-muted'}`}>
                        <SafeIcon icon={s.icon} className="w-5 h-5" />
                      </div>
                      <span className={`text-[10px] font-black uppercase tracking-widest text-center ${defaultSoundscape === s.id ? 'text-accent-navy' : 'text-text-muted'}`}>
                        {s.label}
                      </span>
                      {defaultSoundscape === s.id && (
                        <div className="w-5 h-5 rounded-full bg-accent-gold flex items-center justify-center">
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                <div className="space-y-3 pt-2">
                  <ToggleRow
                    label="Auto-Play Soundscape on Memorial Load"
                    desc="The selected soundscape begins playing automatically when a visitor opens the memorial page."
                    value={soundscapeAutoplay}
                    onChange={setSoundscapeAutoplay}
                  />
                  <ToggleRow
                    label="Allow Owners to Change Soundscape"
                    desc="Memorial creators can choose a different ambient track or disable audio entirely."
                    value={allowSoundscapeChange}
                    onChange={setAllowSoundscapeChange}
                    tag="Recommended"
                  />
                </div>

                {/* Summary */}
                <div className="bg-accent-navy rounded-[24px] p-6 flex items-center gap-4">
                  <div className="p-3 bg-white/10 rounded-2xl flex-shrink-0">
                    <SafeIcon icon={SOUNDSCAPES.find(s => s.id === defaultSoundscape)?.icon || FiVolumeX} className="w-5 h-5 text-accent-gold" />
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Active Default</p>
                    <p className="text-white font-serif text-lg">{SOUNDSCAPES.find(s => s.id === defaultSoundscape)?.label}</p>
                    <p className="text-white/50 text-[10px] mt-0.5">
                      {soundscapeAutoplay ? 'Auto-plays on page load' : 'Manual play — visitor controls audio'}
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* Audio Upload Presets */}
              <SectionCard icon={FiUploadCloud} title="Upload Soundscape Presets" subtitle="Add audio tracks for memorial creators to choose from">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {SOUNDSCAPE_CATEGORIES.map(cat => (
                    <div key={cat.id} className="p-5 border border-brand-dark rounded-[20px] bg-brand-light flex flex-col justify-between">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white rounded-xl shadow-sm border border-brand-dark">
                          <SafeIcon icon={cat.icon} className="w-5 h-5 text-accent-gold" />
                        </div>
                        <div>
                          <h4 className="font-bold text-sm text-accent-navy">{cat.label}</h4>
                          <p className="text-[10px] text-text-muted mt-0.5">Upload preset tracks</p>
                        </div>
                      </div>
                      
                      {urlInputCat === cat.id ? (
                        <div className="space-y-2 mt-2 bg-white p-3 rounded-xl border border-brand-dark shadow-sm">
                          <input 
                            type="text" 
                            placeholder="Track Name" 
                            value={urlInputLabel} 
                            onChange={e => setUrlInputLabel(e.target.value)} 
                            className="w-full text-xs px-3 py-2 border border-brand-dark focus:border-accent-gold rounded-lg outline-none bg-brand-light" 
                          />
                          <input 
                            type="url" 
                            placeholder="Audio URL (https://...)" 
                            value={urlInputValue} 
                            onChange={e => setUrlInputValue(e.target.value)} 
                            className="w-full text-xs px-3 py-2 border border-brand-dark focus:border-accent-gold rounded-lg outline-none bg-brand-light" 
                          />
                          <div className="flex gap-2 pt-1">
                            <button 
                              onClick={() => setUrlInputCat(null)} 
                              className="flex-1 py-1.5 border border-brand-dark rounded-lg text-[9px] font-black uppercase tracking-widest text-text-muted hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button 
                              onClick={() => handleUrlSubmit(cat.id)} 
                              disabled={uploadingAudioCat === cat.id} 
                              className="flex-1 py-1.5 bg-accent-gold text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                            >
                              {uploadingAudioCat === cat.id ? <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin"/> : 'Save URL'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 relative mt-2">
                          <label className="btn-secondary py-2.5 px-4 text-[10px] font-black uppercase tracking-widest w-full justify-center inline-flex items-center gap-2 cursor-pointer border-brand-dark bg-white hover:border-accent-gold transition-all rounded-xl">
                            {uploadingAudioCat === cat.id ? (
                              <div className="w-4 h-4 border-2 border-accent-navy/30 border-t-accent-navy rounded-full animate-spin" />
                            ) : (
                              <><SafeIcon icon={FiUploadCloud} className="w-4 h-4" /> Upload File</>
                            )}
                            <input type="file" accept="audio/*" className="hidden" onChange={(e) => handleAudioUpload(e, cat.id)} disabled={uploadingAudioCat !== null} />
                          </label>
                          <button 
                            onClick={() => { setUrlInputCat(cat.id); setUrlInputLabel(''); setUrlInputValue(''); }} 
                            className="btn-secondary py-2.5 px-4 text-[10px] font-black uppercase tracking-widest w-full justify-center inline-flex items-center gap-2 border-brand-dark bg-transparent hover:bg-white hover:border-accent-navy transition-all text-text-muted rounded-xl"
                          >
                            <SafeIcon icon={FiCloud} className="w-4 h-4" /> Add via URL
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </>
          )}

          {/* ── FEATURES ────────────────────────────────────────────────── */}
          {activeSection === 'features' && (
            <>
              <SectionCard icon={FiSliders} title="Default Feature Toggles" subtitle="Sections enabled by default on every new memorial">
                <div className="space-y-3">
                  <ToggleRow label="Guestbook"    desc="Allow visitors to sign the memorial guestbook."            value={enableGuestbook}   onChange={setEnableGuestbook}   tag="Recommended" />
                  <ToggleRow label="Photo Gallery" desc="Enable the media gallery tab for photo and video uploads." value={enableGallery}     onChange={setEnableGallery}     tag="Recommended" />
                  <ToggleRow label="Donations"     desc="Show the donation progress widget on the memorial page."  value={enableDonations}   onChange={setEnableDonations}   />
                  <ToggleRow label="Live Streaming" desc="Enable the live stream scheduling and broadcast tab."    value={enableLiveStream}  onChange={setEnableLiveStream}  />
                  <ToggleRow label="Milestones"    desc="Show anniversary and life milestone countdowns."          value={enableMilestones}  onChange={setEnableMilestones}  tag="Recommended" />
                  <ToggleRow label="Visitor Map"   desc="Display an interactive world map of tribute origins."    value={enableVisitorMap}  onChange={setEnableVisitorMap}  />
                </div>
              </SectionCard>

              <SectionCard icon={FiBookOpen} title="Default Tribute Layout" subtitle="How tributes are displayed on new memorials">
                <div className="space-y-3">
                  {TRIBUTE_LAYOUTS.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setTributeLayout(l.id)}
                      className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${tributeLayout === l.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-brand-light hover:border-accent-gold/50'}`}
                    >
                      <div className="flex-grow">
                        <p className={`font-bold text-sm ${tributeLayout === l.id ? 'text-accent-navy' : 'text-text-muted'}`}>{l.label}</p>
                        <p className="text-[10px] text-text-muted mt-0.5">{l.desc}</p>
                      </div>
                      {tributeLayout === l.id && (
                        <div className="w-6 h-6 rounded-full bg-accent-gold flex items-center justify-center flex-shrink-0">
                          <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Active features summary */}
                <div className="bg-accent-navy rounded-[24px] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-4">Enabled by Default</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { on: enableGuestbook,  label: 'Guestbook'    },
                      { on: enableGallery,    label: 'Gallery'       },
                      { on: enableDonations,  label: 'Donations'     },
                      { on: enableLiveStream, label: 'Live Stream'   },
                      { on: enableMilestones, label: 'Milestones'    },
                      { on: enableVisitorMap, label: 'Visitor Map'   },
                    ].map(({ on, label }) => (
                      <div key={label} className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${on ? 'bg-accent-gold' : 'bg-white/20'}`} />
                        <span className={`text-[10px] ${on ? 'text-white' : 'text-white/30 line-through'}`}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </SectionCard>
            </>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-brand-dark text-text-muted rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-red-300 hover:text-red-500 transition-all"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
          Reset to Defaults
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Memorial Defaults
        </button>
      </div>

      <SaveToast show={showToast} message={toastMsg} />
    </div>
  );
};

export default MemorialDefaults;